/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import { ccReelEvent, GetReelEventName, ReelEvent } from '../Reel/ReelEvent';
import EventActionComponent from '../Utility/Component/Action/EventActionComponent';
import EffectSequence from './EffectSequence';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/** Class used to trigger [[EffectSequence]] based on event. */
@cc._decorator.ccclass
export default class EventEffectSequence extends EventActionComponent
{
    /** [[ReelEvent]] enum shortcut for onAction. */
    @property({ type: ccReelEvent, visible: true })
    private _onReelEvent: ReelEvent = ReelEvent.OnAnticipation;

    /** onAction event name. */
    @property()
    public get OnEventName(): string { return this.getOnEventName(this._onReelEvent); }

    /** onAction event override. */
    @property({ visible: true })
    private _onEventOverride: string = '';

    /** [[ReelEvent]] enum shortcut for offAction. */
    @property({ type: ccReelEvent, visible: true })
    private _offReelEvent: ReelEvent = ReelEvent.OffAnticipation;

    /** offAction event name. */
    @property()
    public get OffEventName(): string { return this.getOffEventName(this._offReelEvent);  }

    @property({ visible: true })
    /** onAction event override. */
    private _offEventOverride: string = '';

    /** True when onAction should not if already on. */
    @property()
    protected runOnce: boolean = true;

    /** onAction [[EventSequence]] */
    private _onEventEffects: EffectSequence = null;
    /** offAction [[EventSequence]] */
    private _offEventEffects: EffectSequence = null;

    /** @hidden */
    private _isComplete: boolean = false;
    /** Nodes used for maniuplation -- not part of [[EffectSequence]]s. */
    private _visibleNodes: cc.Node[] = [];

    private _isOn: boolean = false;

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.configureChildren();
    }

    /** @hidden */
    private configureChildren(): void
    {
        let effects: EffectSequence[] = this.node.getComponentsInChildren(EffectSequence);

        this._onEventEffects = effects[0];
        this._offEventEffects = effects[1];

        for (let childNode of this.node.children)
        {
            if ((this._onEventEffects && childNode === this._onEventEffects.node)
                ||  (this._offEventEffects && childNode === this._offEventEffects.node)
                || (this.TargetNode && childNode === this.TargetNode.Value))
            {
                continue;
            }

            this._visibleNodes.push(childNode);
        }
    }

    /** @hidden */
    protected firstUpdate(dt: number): void
    {
        super.firstUpdate(dt);

        this.setVisible(false);
    }

    /** Public function to trigger onAction. */
    public Action = (): void =>
    {
        this.action();
    }

    /** Trigger onAction. */
    protected action = (): void =>
    {
        if (this._isComplete && this.runOnce) { return; }

        this.setVisible(true);

        if (this._onEventEffects) { this._onEventEffects.Trigger(); }

        this._isOn = true;
        this._isComplete = true;
    }

    /** Public function to trigger offAction. */
    public OffAction = (): void =>
    {
        this.offAction();
    }

    /** Trigger offAction */
    protected offAction = (): void =>
    {
        if (!this._isOn) { return; }

        if (this._onEventEffects) { this._onEventEffects.Stop(); }
        if (this._offEventEffects) { this._offEventEffects.Trigger(); }

        this._isComplete = false;
        this._isOn = false;

        this.setVisible(false);
    }

    /** @hidden */
    private setVisible(to: boolean): void
    {
        this._visibleNodes.forEach(node => node.active = to);
    }

    /** Helper to get onAction [[ReelEvent]] or CustomEvent override. */
    private getOnEventName(reelEvent: ReelEvent): string
    {
        if (this._onReelEvent === ReelEvent.Custom && this._onEventOverride) { return this._onEventOverride; }
        else { return GetReelEventName(reelEvent); }
    }

    /** Helper to get offAction [[ReelEvent]] or CustomEvent override. */
    private getOffEventName(reelEvent: ReelEvent): string
    {
        if (this._onReelEvent === ReelEvent.Custom && this._offEventOverride) { return this._offEventOverride; }
        else { return GetReelEventName(reelEvent); }
    }
}
